import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import styles from '../GeoBrowse.module.css';

export type BrowseColumn<T> = {
  id: string;
  label: string;
  accessor?: (row: T) => string | number | boolean;
  sortValue?: (row: T) => string | number;
  filterValue?: (row: T) => string | number | boolean;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  filterable?: boolean;
};

export type BrowseDataTableProps<T> = {
  rows: T[];
  columns: BrowseColumn<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** When true, row gets highlighted styling (e.g. genes selected in Gene Details) */
  isRowHighlighted?: (row: T) => boolean;
  searchPlaceholder?: string;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  defaultSort?: { columnId: string; direction: 'asc' | 'desc' };
  searchFilter?: (row: T, query: string) => boolean;
  enableColumnFilters?: boolean;
};

type SortState = { columnId: string; direction: 'asc' | 'desc' } | null;

function compareValues(a: string | number | boolean, b: string | number | boolean): number {
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function getColumnText<T>(row: T, col: BrowseColumn<T>): string {
  const val = col.filterValue?.(row) ?? col.sortValue?.(row) ?? col.accessor?.(row);
  if (val === undefined || val === null) return '';
  return String(val);
}

export default function BrowseDataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  isRowHighlighted,
  searchPlaceholder = 'Search…',
  pageSizeOptions = [10, 25, 50, 100],
  defaultPageSize = 25,
  defaultSort = null,
  searchFilter,
  enableColumnFilters = true,
}: BrowseDataTableProps<T>) {
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(0);
  const [globalSearch, setGlobalSearch] = useState('');
  const [sort, setSort] = useState<SortState>(defaultSort);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  const filteredRows = useMemo(() => {
    let result = rows;
    const q = globalSearch.trim().toLowerCase();

    if (q) {
      result = searchFilter
        ? result.filter(row => searchFilter(row, q))
        : result.filter(row =>
            columns.some(col => getColumnText(row, col).toLowerCase().includes(q)),
          );
    }

    result = result.filter(row =>
      columns.every(col => {
        if (col.filterable === false || !enableColumnFilters) return true;
        const filterQuery = (columnFilters[col.id] ?? '').trim().toLowerCase();
        if (!filterQuery || filterQuery === 'all') return true;
        return getColumnText(row, col).toLowerCase().includes(filterQuery);
      }),
    );

    if (sort) {
      const col = columns.find(c => c.id === sort.columnId);
      if (col) {
        result = [...result].sort((a, b) => {
          const getVal = (row: T) => {
            if (col.sortValue) return col.sortValue(row);
            if (col.accessor) return col.accessor(row);
            return '';
          };
          const cmp = compareValues(getVal(a), getVal(b));
          return sort.direction === 'asc' ? cmp : -cmp;
        });
      }
    }

    return result;
  }, [rows, columns, globalSearch, sort, columnFilters, searchFilter, enableColumnFilters]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = filteredRows.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const rangeStart = filteredRows.length === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd = Math.min((safePage + 1) * pageSize, filteredRows.length);

  const handleSort = (columnId: string) => {
    setSort(prev => {
      if (prev?.columnId === columnId) {
        return prev.direction === 'asc' ? { columnId, direction: 'desc' } : null;
      }
      return { columnId, direction: 'asc' };
    });
  };

  const setColumnFilter = (columnId: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [columnId]: value }));
    setPage(0);
  };

  return (
    <div className={styles.tableCard}>
      <div className={styles.tableToolbar}>
        <label className={styles.rowsControl}>
          Show
          <select
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
          >
            {pageSizeOptions.map(n => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          entries
        </label>
        <label className={styles.searchWrap}>
          <span className={styles.searchLabel}>Search:</span>
          <input
            className={styles.searchInputBare}
            type="search"
            placeholder={searchPlaceholder}
            value={globalSearch}
            onChange={e => {
              setGlobalSearch(e.target.value);
              setPage(0);
            }}
          />
        </label>
      </div>

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map(col => {
                const isSortable = col.sortable !== false;
                const isSorted = sort?.columnId === col.id;
                return (
                  <th
                    key={col.id}
                    className={isSortable ? styles.thSortable : undefined}
                    aria-sort={
                      isSorted
                        ? sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : isSortable
                          ? 'none'
                          : undefined
                    }
                    onClick={() => isSortable && handleSort(col.id)}
                  >
                    {col.label}
                    {isSorted && (
                      <span className={styles.sortIcon}>{sort.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </th>
                );
              })}
            </tr>
            {enableColumnFilters && (
              <tr className={styles.tableFilterRow}>
                {columns.map(col => (
                  <th key={`${col.id}-filter`}>
                    {col.filterable !== false ? (
                      <input
                        type="search"
                        className={styles.columnFilterInput}
                        placeholder="All"
                        value={columnFilters[col.id] ?? ''}
                        aria-label={`Filter ${col.label}`}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setColumnFilter(col.id, e.target.value)}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.tableEmpty}>
                  No matching rows.
                </td>
              </tr>
            ) : (
              pageRows.map(row => {
                const highlighted = isRowHighlighted?.(row) ?? false;
                const rowClass = [
                  onRowClick ? styles.clickableRow : '',
                  highlighted ? styles.highlightedRow : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                <tr
                  key={rowKey(row)}
                  className={rowClass || undefined}
                  data-highlighted={highlighted || undefined}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map(col => (
                    <td key={col.id}>
                      {col.render ? col.render(row) : String(col.accessor?.(row) ?? '')}
                    </td>
                  ))}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.tableFooter}>
        <span>
          Showing {rangeStart} to {rangeEnd} of {filteredRows.length} entries
          {filteredRows.length !== rows.length && ` (filtered from ${rows.length})`}
        </span>
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={safePage === 0}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>
          <span>
            Page {safePage + 1}/{totalPages}
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
